import { mathjax } from 'mathjax-full/js/mathjax';
import { TeX } from 'mathjax-full/js/input/tex';
import { SVG } from 'mathjax-full/js/output/svg';
import { liteAdaptor } from 'mathjax-full/js/adaptors/liteAdaptor';
import { RegisterHTMLHandler } from 'mathjax-full/js/handlers/html';
import 'mathjax-full/js/input/tex/base/BaseConfiguration';
import 'mathjax-full/js/input/tex/ams/AmsConfiguration';

/**
 * Inline TeX in labels: `$x_1$`, `$\alpha^2$`. Follows Pandoc's rule so prices read as text:
 * the opening `$` must be followed by a non-space, the closing `$` preceded by a non-space
 * and not followed by a digit. `\$` is a literal dollar.
 */
const MATH_PATTERN = /(?<!\\)\$(?!\s)((?:\\.|[^$\\])*?(?:\\.|[^\s$\\]))\$(?!\d)/g;

const unescapeDollars = (text) => text.replace(/\\\$/g, '$');

/** Split `text` into `{ type: 'text' | 'math', value }` runs. */
export const parseMath = (text) => {
  const source = String(text ?? '');
  const parts = [];
  let last = 0;
  source.replace(MATH_PATTERN, (match, tex, offset) => {
    if (offset > last) parts.push({ type: 'text', value: unescapeDollars(source.slice(last, offset)) });
    parts.push({ type: 'math', value: tex });
    last = offset + match.length;
    return match;
  });
  if (last < source.length) parts.push({ type: 'text', value: unescapeDollars(source.slice(last)) });
  return parts;
};

export const hasMath = (text) => parseMath(text).some((part) => part.type === 'math');

let converter = null;
const getConverter = () => {
  if (!converter) {
    const adaptor = liteAdaptor();
    RegisterHTMLHandler(adaptor);
    const doc = mathjax.document('', {
      InputJax: new TeX({ packages: ['base', 'ams'] }),
      // glyphs as inline paths: no shared <defs>, so labels survive SVG export on their own
      OutputJax: new SVG({ fontCache: 'none' }),
    });
    converter = { adaptor, doc };
  }
  return converter;
};

/** TeX glyphs have a smaller x-height than Arial; scale them up to sit evenly with label text. */
const MATH_SCALE = 1.15;

const cache = new Map();

/**
 * Typeset inline TeX as SVG. Dimensions are in em, measured from the baseline:
 * `{ width, height, depth, viewBox, body }`, or null when the TeX does not parse.
 */
export const typeset = (tex) => {
  if (cache.has(tex)) return cache.get(tex);
  let result = null;
  try {
    const { adaptor, doc } = getConverter();
    const container = doc.convert(tex, { display: false });
    const svg = adaptor.firstChild(container);
    const body = adaptor.innerHTML(svg);
    if (!body.includes('data-mjx-error')) {
      const viewBox = adaptor.getAttribute(svg, 'viewBox');
      const [, minY, w, h] = viewBox.split(/\s+/).map(Number);
      result = {
        width: (w / 1000) * MATH_SCALE,
        height: (-minY / 1000) * MATH_SCALE,
        depth: ((h + minY) / 1000) * MATH_SCALE,
        viewBox,
        body,
      };
    }
  } catch (error) {
    result = null;
  }
  cache.set(tex, result);
  return result;
};
